import { useState } from "react";
import { useMutation } from "@apollo/client";
import {
  ClassroomBooking,
  ClassroomBookingCrudResult,
  SessionAssignmentBookingInput,
} from "@models/ISchema";
import { classroomBookingsUpdate } from "../graphql/classroomBookingsUpdate.mutation";
import { errors } from "@models/Incidences/SessionByType";
import { toast } from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import { getEditedClassroomAssignedDates } from "../utils/classroomEvents";

interface Props {
  origin: string;
  scenario: string;
  onSuccess?: (booking: ClassroomBooking) => void;
}

type ReturnType = [
  { result: any; error: boolean; isLoading: boolean },
  (data: any, dryRun?: boolean, skipValidations?: boolean, onlyDetails?: boolean) => Promise<void>,
];

const useClassroomBookingsMutation = ({ origin, scenario, onSuccess }: Props): ReturnType => {
  const { t } = useTranslation();
  const [classroomBookingsMutation] = useMutation(classroomBookingsUpdate, {});
  const [result, setResult] = useState<ClassroomBookingCrudResult>({});
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const buildInput = (
    booking: ClassroomBooking,
    sessionAssignmentBookings: SessionAssignmentBookingInput[],
    dryRun?: boolean,
    skipValidations?: boolean,
    onlyDetails?: boolean,
  ) => ({
    dryRun,
    skipValidations,
    onlyDetails,
    changesets: {
      creates: [],
      deletes: [],
      updates: [
        {
          sessionAssignmentBookings,
          classroomBookingId: booking?.id,
          responsibleId: booking?.responsible?.id,
          capacity: booking?.capacity,
          isRecurrent: booking?.isRecurrent,
          title: booking?.title,
          description: booking?.description,
          classroomBookingTypeId: booking?.type?.id,
          programId: booking?.program,
        },
      ],
    },
  });

  const submitClassroomBookingsMutation = async (
    booking: ClassroomBooking & { classroomAssignedDates?: SessionAssignmentBookingInput[] },
    dryRun?: boolean,
    skipValidations?: boolean,
    onlyDetails?: boolean,
  ) => {
    try {
      setIsLoading(true);
      const singleClassroomAssignedDates = booking?.classroom
        ? getEditedClassroomAssignedDates([
            {
              classroom: booking?.classroom,
              dates: booking?.classroom?.dateAvailability,
            },
          ])
        : [];

      const variables = {
        scenarioId: scenario,
        originId: origin,
        input: buildInput(
          booking,
          booking?.classroomAssignedDates ??
            ((singleClassroomAssignedDates as unknown) as SessionAssignmentBookingInput[]),
          dryRun,
          skipValidations,
          onlyDetails,
        ),
      };
      const { data: responseData } = await classroomBookingsMutation({ variables });

      const response = responseData?.cube?.classroomBookingsCrud as ClassroomBookingCrudResult;
      setResult(responseData?.cube?.classroomBookingsCrud);

      if (!response?.commited) {
        const validationErrorType = response?.payload?.updates?.find(
          update => update?.validationErrors?.length > 0,
        )?.validationErrors[0]?.__typename;
        const errorMessage = errors?.[validationErrorType];

        errorMessage &&
          toast.error(
            `${t("booking.classroom-search.notifications.complementary-error")} ${errorMessage}`,
            {
              autoClose: 3000,
              toastId: "general-error-booking",
            },
          );
      } else {
        onSuccess?.(response?.payload?.updates?.[0]?.booking);
      }
    } catch (error) {
      setError(true);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return [{ result, error, isLoading }, submitClassroomBookingsMutation];
};

export default useClassroomBookingsMutation;
